import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from './index';
import type { TFunction } from 'i18next';
import { normalizeToI18n, UiLanguage, I18nBaseLang } from '../i18n/languages';
import { Product } from 'domains/catalog/aggregates/Product';
import { IOrderItem } from 'modules/cart/domain/models/IOrderItem';

export type ChangeLangInput = UiLanguage | I18nBaseLang;

type Return = {
  t: TFunction;
  i18n: typeof i18n;
  ready: boolean;
  language: string | undefined;
  changeLanguage: (lng: ChangeLangInput) => Promise<void>;
  getProductName: (product: Product | IOrderItem, lang?: string) => string;
};

const SAVED_LANG_KEY = 'app.lang';

export function useTranslation(): Return {
  const [, setTick] = useState(0);
  const [ready, setReady] = useState(!!(i18n as any).isInitialized);
  const [language, setLanguage] = useState<string | undefined>(i18n.language);

  useEffect(() => {
    const onInit = () => setReady(true);
    const onChange = (lng?: string) => {
      setLanguage(lng || i18n.language);
      setTick((v) => v + 1);
    };
    
    if (!(i18n as any).isInitialized) (i18n as any).on('initialized', onInit);
    (i18n as any).on('languageChanged', onChange);
    (i18n as any).on('loaded', onChange);
    
    // restaura do localStorage (opcional)
    const saved = localStorage.getItem(SAVED_LANG_KEY);
    if (saved && saved !== i18n.language) i18n.changeLanguage(saved);
    
    return () => {
      (i18n as any).off('initialized', onInit);
      (i18n as any).off('languageChanged', onChange);
      (i18n as any).off('loaded', onChange);
    };
  }, []);

  const getProductName = useCallback((product: Product | IOrderItem) => {
    return product.translations.find((it) => it.lang === language)?.name ?? product.name;
  }, [language]);

  const t = useMemo(() => (i18n.t as any).bind(i18n) as TFunction, []);

  const changeLanguage = useCallback(async (lng: ChangeLangInput) => {
    const target = normalizeToI18n(lng);
    await i18n.changeLanguage(target);
    try { localStorage.setItem(SAVED_LANG_KEY, target); } catch {}
  }, []);

  return { t, i18n, ready, language, changeLanguage, getProductName };
}
